import { Client, Coach } from "@/types/page";
import { responseStatus } from "@/utils/apiErrors";
import { getCookie } from "@/utils/CookieJar";
import { toSpaUrl } from "@/utils/hosts";
import { queryClient } from "@/utils/queryClient";
import { queryOptions, useQuery } from "@tanstack/react-query";
import axios from "axios";

export type Identity = {
    user: Client | Coach | null;
    twoFactorPassed: boolean;
    isImpersonating: boolean;
    impersonatorId: number | null;
    showEmailVerificationReminder: boolean;
    justRegistered: boolean;
    // A session holding a decoy client instead of an account (an email that cannot sign up again); AppRouter keeps it on the verify page.
    mustVerifyEmail: boolean;
};

const loggedOut: Identity = {
    user: null,
    twoFactorPassed: false,
    isImpersonating: false,
    impersonatorId: null,
    showEmailVerificationReminder: false,
    justRegistered: false,
    mustVerifyEmail: false,
};

const RETRIES_WITHOUT_AN_ANSWER = 3;

// Query keys hold no user, so a session that ends without logout() (a 401, a logout in another tab) must not leave its answers to the next user.
function forgetQueriesOfAnEndedSession(identity: Identity): Identity {
    const cachedUser = queryClient.getQueryData(identityQuery.queryKey)?.user;
    if (
        cachedUser &&
        (cachedUser.type !== identity.user?.type ||
            cachedUser.id !== identity.user.id)
    ) {
        queryClient.removeQueries({
            predicate: (query) =>
                query.queryKey[0] !== identityQuery.queryKey[0],
        });
    }

    return identity;
}

/*
 * Drops a session that ended elsewhere but keeps the queries on screen, so the
 * next login starts from nobody. Query.reset() re-renders what shows the old
 * identity, which a removal would not, and fetches nothing, unlike resetQueries,
 * but cancels a request in flight: one on its way forgets the session itself.
 */
export function forgetEndedSession() {
    const identity = queryClient
        .getQueryCache()
        .find({ queryKey: identityQuery.queryKey, exact: true });
    if (identity?.state.fetchStatus !== "idle") {
        return;
    }

    identity.reset();
    queryClient.removeQueries({
        predicate: (query) => query.getObserversCount() === 0,
    });
}

/*
 * Logged out means an answer without a user, or a 401, which the middleware
 * that has just ended the session (UserContinuity, AuthenticateSession)
 * answers. AppRouter shows the error page for any other failed first load, once
 * requests that got no answer at all (a dropped connection, a WAF challenge)
 * have been retried. A failed refreshIdentity() fails straight away, leaving
 * its caller to decide how to go on, and keeps the last answer on screen; the
 * query cache still reports the failure.
 */
export const identityQuery = queryOptions({
    queryKey: ["identity"],
    queryFn: () =>
        axios
            .get<Identity>(route("identity"))
            .then((response) => response.data)
            .catch((error: unknown) => {
                if (responseStatus(error) === 401) {
                    return loggedOut;
                }
                throw error;
            })
            .then(forgetQueriesOfAnEndedSession),
    staleTime: Infinity,
    retry: (failureCount, error) =>
        responseStatus(error) === null &&
        failureCount < RETRIES_WITHOUT_AN_ANSWER,
    throwOnError: (_, query) => query.state.data === undefined,
});

const LOGGED_IN_MARKER_COOKIE = "logged_in";

/*
 * LoggedInMarkerCookieMiddleware keeps this JS-readable cookie in sync with the
 * session. Without it the visitor is logged out, so logged out pages skip the
 * identity request entirely.
 */
export function hasLoggedInMarker(): boolean {
    return getCookie<string | null>(LOGGED_IN_MARKER_COOKIE, null) !== null;
}

/*
 * Logged out until the first answer arrives. Logged in pages never see that
 * placeholder: AppRouter waits for identity before rendering them. Identity is
 * one answer for the whole app, so these hooks read the app's query client
 * directly and work in any tree. They never throw, because they also run above
 * AppRouter's error boundary (AnalyticsProvider) and inside its error screen.
 */
export function useIdentity(): Identity {
    return (
        useQuery(
            {
                ...identityQuery,
                enabled: hasLoggedInMarker(),
                throwOnError: false,
            },
            queryClient,
        ).data ?? loggedOut
    );
}

export function useIsIdentityLoaded(): boolean {
    const isLoggedInByMarker = hasLoggedInMarker();
    const { data } = useQuery(
        { ...identityQuery, enabled: isLoggedInByMarker, throwOnError: false },
        queryClient,
    );

    return !isLoggedInByMarker || data !== undefined;
}

export function useUser(): Client | Coach {
    const { user } = useIdentity();
    if (!user) {
        throw new Error("useUser() needs a logged in user.");
    }

    return user;
}

export function useClient(): Client {
    const user = useUser();
    if (user.type !== "client") {
        throw new Error("useClient() needs a logged in client.");
    }

    return user;
}

export function useCoach(): Coach {
    const user = useUser();
    if (user.type !== "coach") {
        throw new Error("useCoach() needs a logged in coach.");
    }

    return user;
}

export function refreshIdentity(): Promise<Identity> {
    return queryClient.fetchQuery({
        ...identityQuery,
        staleTime: 0,
        retry: false,
    });
}

export function currentIdentity(): Identity {
    return queryClient.getQueryData(identityQuery.queryKey) ?? loggedOut;
}

/*
 * A full page load after logging out drops every provider's and query's state
 * that belonged to the user. Until the browser leaves the page it stays as it
 * is: emptying the cache now would leave its components without a user.
 */
export async function logout() {
    await axios.post(route("logout"));
    // The back-forward cache would otherwise bring this page back on Back, still showing the user.
    window.addEventListener(
        "pageshow",
        (event) => {
            if (event.persisted) {
                window.location.reload();
            }
        },
        { once: true },
    );
    window.location.assign(
        toSpaUrl(window.isMobileApp ? route("client.login") : route("home")),
    );
}

export function onIdentityChange(callback: (identity: Identity) => void) {
    return queryClient.getQueryCache().subscribe((event) => {
        if (
            event.type === "updated" &&
            event.action.type === "success" &&
            event.query.queryKey[0] === identityQuery.queryKey[0]
        ) {
            callback(currentIdentity());
        }
    });
}
